import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { trackOnce, invoiceProperties, paidInvoiceEvent } from "/imports/libs/analytics";
import { peekResumeIntent, clearResumeIntent } from "/imports/libs/resumeIntent";
import { createWidgetTheme } from "/imports/ui/styles/theme";
import CheckoutTelrCard from "/imports/ui/views/CheckoutTelrCard";
import AddToCalendarMenu from "/imports/ui/components/shared/AddToCalendarMenu";
import CardDeclinedTip from "/imports/ui/components/shared/CardDeclinedTip";

export default function CheckoutTelrDoneView() {
  const { chargeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const widgetSlug = sessionStorage.getItem("widget_slug");

  // Every exit from this page drops the resume intent, whichever way the client
  // leaves. It is the last step of the trip it was stored for, and a survivor
  // would greet an unrelated later purchase with a stale class to go back to.
  const handleGoHome = () => {
    clearResumeIntent();
    navigate(widgetSlug ? `/${widgetSlug}` : "/");
  };

  // Where this pack was bought to be spent, read once so a refresh of this page
  // keeps the way back. Only a confirmed payment earns the return trip — a
  // declined or cancelled attempt leaves the client with no pack to redeem, so
  // the intent is dropped and they get the usual way home.
  const [resume] = useState(() => peekResumeIntent());
  const handleResume = () => {
    clearResumeIntent();
    navigate(resume.joinAfter ? `/${resume.slug}/classes/${resume.joinAfter}` : resume.backTo);
  };
  const url = new URL(window.location.href);
  const payId = url.searchParams.get("PAYID");

  const isCancelledRoute = location.pathname.endsWith("/cancelled");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);

  // Partner-link tabs are opened by the client from WhatsApp/email — there is no
  // widget home to return to and the browser won't let us close the tab, so just
  // tell the client they can close it themselves.
  const closeMessage = (
    <Typography variant="body2" color="text.secondary">
      You can now close this tab.
    </Typography>
  );

  // Invoice was settled out-of-band (e.g. dashboard "Complete Payment" via
  // Visa/Cash) — this Telr attempt did NOT pay it. Suppress the retry flow so
  // we don't over-collect. When this same payment is the one that paid the
  // invoice, fall through to the regular "Payment was successful" branch.
  const invoicePaid = data?.invoice?.status === "PAID" || data?.invoice?.status === "SETTLED";
  const invoiceAlreadyPaid = invoicePaid && data?.payment?.status !== "PAID";

  // The client completed payment on Telr after the booking was already cancelled,
  // so the backend captured then auto-refunded it (payment left CANCELED with
  // refundData). Tell them their money was returned instead of the misleading
  // "contact the venue for a new payment link".
  const wasAutoRefunded = !!data?.payment?.refundData;
  const refundCurrency = (data?.payment?.currency || data?.saas?.currency || "AED").toUpperCase();
  const refundAmountText =
    data?.payment?.amount != null && data?.payment?.amount !== ""
      ? `${refundCurrency} ${(parseFloat(data.payment.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "";

  const handleRetry = async () => {
    setRetryError(null);
    setRetrying(true);
    try {
      const res = await api.retryTelrPayment(chargeId);
      if (!res?.chargeId) {
        throw new Error("Could not start a new payment attempt.");
      }
      navigate(`/checkout/telr/${res.chargeId}`);
    } catch (err) {
      setRetryError(err?.message || "Could not start a new payment attempt. Please contact the venue.");
      setRetrying(false);
    }
  };

  const theme = useMemo(() => createWidgetTheme({ primaryColor: data?.widgetColor || undefined }), [data?.widgetColor]);

  useEffect(() => {
    if (!payId || isCancelledRoute) return;
    api.recordTelrTransaction(chargeId, payId).catch(() => {});
  }, [chargeId, payId, isCancelledRoute]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Always reconcile with Telr first — including on the /cancelled route —
        // so an abandoned attempt is settled to CANCELED instead of lingering as
        // PENDING. The DB status read below is then authoritative.
        await api.checkTelrStatus(chargeId).catch(() => {});
        const res = await api.getTelrCharge(chargeId);
        if (cancelled) return;
        setData(res);
        applyBranding({ name: res?.saas?.name, logoUrl: res?.saas?.logoUrl });

        const status = res?.payment?.status;
        const paid = status === "PAID";
        setIsPaid(paid);
        // CANCELED -> "cancelled"; FAILED -> "declined". When Telr's status could
        // not be confirmed (still PENDING) fall back to the route Telr returned to.
        const showAsCanceled = status === "CANCELED" || (!paid && status !== "FAILED" && isCancelledRoute);
        setIsCanceled(showAsCanceled);

        // Telr authorised the card but hasn't confirmed capture yet (status still
        // PENDING on the return route) — surface "processing" rather than a
        // misleading decline. The webhook / next status check settles it shortly.
        setIsProcessing(!paid && !showAsCanceled && status !== "FAILED");

        // This page is reloaded and revisited; each outcome counts once per charge.
        // "Processing" isn't an outcome yet — it settles server-side, unseen here.
        // payment_completed is the purchase Meta / Google are credited with, so it
        // is only sent for a sale the widget made, never a club pay link.
        const props = invoiceProperties(res);
        if (paid) {
          if (props.from_widget) trackOnce(`paid_${chargeId}`, "payment_completed", props);
          trackOnce(`done_${props.invoice_id || chargeId}`, paidInvoiceEvent(props), { ...props, paid_online: true });
        } else if (showAsCanceled || status === "FAILED") {
          const reason = res?.payment?.refundData ? "auto_refunded" : showAsCanceled ? "cancelled" : "declined";
          trackOnce(`failed_${chargeId}`, "payment_failed", { ...props, reason });
        }

        // The user explicitly hit Cancel on the Telr page — release the slot now
        // instead of holding it until the 10-min sweep. Fire-and-forget; if it
        // fails the cron still picks the invoice up later.
        if (showAsCanceled && isCancelledRoute) {
          api.releaseCancelledBooking(chargeId).catch(() => {});
        }
      } catch (_err) {
        // surfaces as "could not load" via empty data
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chargeId, isCancelledRoute]);

  useEffect(() => {
    if (!loading && !isPaid) clearResumeIntent();
  }, [loading, isPaid]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Bookings created in the widget have a widget "home" to return to. Bookings
  // created from the partners dashboard (a payment link sent to the client) do
  // not — the client only ever sees this checkout page, so hide the home button.
  const isWidgetBooking = data?.invoice?.source === "widget-v2";

  // Only on the two paid outcomes. The server signs it once the invoice is paid
  // and it carries an upcoming session; a plain pack purchase has none.
  // Desktop puts it in the left column under the booking details (CheckoutTelrCard's
  // leftPaneContent); the phone keeps it here in the stacked flow, under "confirmed".
  const calendarButton = (
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      <AddToCalendarMenu calendar={data?.calendar} sx={{ mb: 1 }} />
    </Box>
  );
  const desktopCalendarButton = invoiceAlreadyPaid || isPaid ? <AddToCalendarMenu calendar={data?.calendar} /> : null;

  // Only a payment Telr actually declined, and only on the declined pane — not the
  // "already paid" or "refunded" outcomes that win over it below.
  // Same split as the calendar button: left column on desktop, stacked flow on a phone.
  const isFailed = data?.payment?.status === "FAILED" && !invoiceAlreadyPaid && !wasAutoRefunded;
  const desktopDeclinedTip = isFailed ? <CardDeclinedTip clubName={data?.saas?.name} sx={{ mb: 0 }} /> : null;

  let rightPaneContent;
  if (invoiceAlreadyPaid) {
    rightPaneContent = (
      <Box textAlign="center">
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "5rem",
            height: "5rem",
            borderRadius: "50%",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            mb: 2
          }}
        >
          <CheckCircleIcon sx={{ fontSize: "2.75rem", color: "primary.main" }} />
        </Box>
        <Typography variant="h6" fontWeight={600} mb={1}>
          This invoice has already been paid
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          No further payment is required. Contact the venue if you need a receipt.
        </Typography>
        {calendarButton}
        {isWidgetBooking ? (
          <Button variant="contained" fullWidth onClick={handleGoHome}>
            Back to Home
          </Button>
        ) : (
          closeMessage
        )}
      </Box>
    );
  } else if (isPaid) {
    rightPaneContent = (
      <Box textAlign="center">
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "5rem",
            height: "5rem",
            borderRadius: "50%",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            mb: 2
          }}
        >
          <CheckCircleIcon sx={{ fontSize: "2.75rem", color: "primary.main" }} />
        </Box>
        <Typography variant="h6" fontWeight={600} mb={1}>
          Payment was successful
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {resume
            ? resume.joinAfter
              ? "Your pack is ready — pick up the class you were joining."
              : "Your pack is ready — pick up where you left off."
            : "Your booking is confirmed"}
        </Typography>
        {calendarButton}
        {resume ? (
          <>
            <Button variant="contained" fullWidth onClick={handleResume} sx={{ mb: 1 }}>
              {resume.joinAfter ? "Continue to your class" : "Continue booking"}
            </Button>
            <Button variant="text" fullWidth onClick={handleGoHome}>
              Back to Home
            </Button>
          </>
        ) : isWidgetBooking ? (
          <Button variant="contained" fullWidth onClick={handleGoHome}>
            Back to Home
          </Button>
        ) : (
          closeMessage
        )}
      </Box>
    );
  } else if (wasAutoRefunded) {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CancelIcon sx={{ fontSize: "4.5rem", color: "error.main", mb: 2 }} />
        <Typography variant="h6" mb={1}>
          This booking was cancelled
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {refundAmountText ? `Your payment of ${refundAmountText} has been refunded.` : "Your payment has been refunded."} It may take a few business
          days to appear on your statement.
        </Typography>
        {isWidgetBooking ? (
          <Button variant="contained" onClick={handleGoHome} fullWidth>
            Back to Home
          </Button>
        ) : (
          closeMessage
        )}
      </Box>
    );
  } else if (isCanceled) {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CancelIcon sx={{ fontSize: "4.5rem", color: "error.main", mb: 2 }} />
        <Typography variant="h6">Payment was cancelled</Typography>
        <Typography variant="body2" color="text.secondary" mt={1} mb={3}>
          {isWidgetBooking ? "Your slot has been released. You can book again any time." : "Please contact the venue to request a new payment link."}
        </Typography>
        {isWidgetBooking ? (
          <Button variant="contained" onClick={handleGoHome} fullWidth>
            Book again
          </Button>
        ) : (
          closeMessage
        )}
      </Box>
    );
  } else if (isProcessing) {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" mb={1}>
          Your payment is being processed
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          We've received your payment and are waiting for final confirmation from the payment provider. This can take a few moments — please don't pay
          again.{isWidgetBooking ? "" : " Contact the venue if you're unsure."}
        </Typography>
        {isWidgetBooking ? (
          <Button variant="contained" onClick={handleGoHome} fullWidth>
            Back to Home
          </Button>
        ) : (
          closeMessage
        )}
      </Box>
    );
  } else {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <CancelIcon sx={{ fontSize: "4.5rem", color: "error.main", mb: 2 }} />
        <Typography variant="h6" mb={1}>
          Payment was declined
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={isFailed ? { xs: 2, md: 3 } : 3}>
          Try again with a different card
        </Typography>
        {isFailed && (
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <CardDeclinedTip clubName={data?.saas?.name} />
          </Box>
        )}
        {retryError && (
          <Typography variant="body2" color="error.main" mb={2}>
            {retryError}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleRetry}
          disabled={retrying}
          fullWidth
          sx={{ mb: 1 }}
          startIcon={retrying ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {retrying ? "Starting new attempt..." : "Try Again"}
        </Button>
        {isWidgetBooking ? (
          <Button variant="text" onClick={handleGoHome} disabled={retrying} fullWidth>
            Back to Home
          </Button>
        ) : (
          closeMessage
        )}
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CheckoutTelrCard data={data} rightPaneContent={rightPaneContent} leftPaneContent={desktopCalendarButton || desktopDeclinedTip} />
    </ThemeProvider>
  );
}
